import { useState } from "react";
import { FaTrashCan } from "react-icons/fa6";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cancelRideApi, updateRideApi } from "services/auth/rideApis";
import { useNavigate } from "react-router-dom";
import { FiMapPin, FiCalendar, FiClock, FiUser, FiUsers, FiCreditCard, FiNavigation, FiFileText, FiShield } from 'react-icons/fi';
import { RidePreferenceWarningModal } from "./RidePreferenceWarningModal";

const GenderBadge = ({ gender }) => {
    if (!gender) return null;
    const isFemale = gender === "FEMALE";
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
            isFemale 
                ? "bg-pink-100 text-pink-700 border border-pink-200" 
                : "bg-amber-100 text-amber-800 border border-amber-200"
        }`}>
            {isFemale ? "♀" : "♂"} {isFemale ? "Female" : "Male"}
        </span>
    );
};

const DesignationBadge = ({ email }) => {
    if (!email) return null;
    const isStudent = email.endsWith("@muj.manipal.edu");
    const isFaculty = email.endsWith("@jaipur.manipal.edu");
    if (!isStudent && !isFaculty) return null;
    return (
        <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${
            isFaculty 
                ? "bg-purple-100 text-purple-700 border border-purple-200" 
                : "bg-emerald-100 text-emerald-700 border border-emerald-200"
        }`}>
            {isFaculty ? "Faculty" : "Student"}
        </span>
    );
};

export const RideDetailsCard = ({ ride, setEdit, onRideUpdated }) => {
    const navigate = useNavigate();
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [updatingPref, setUpdatingPref] = useState(false);

    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const [date, time] = new Date(ride?.date).toISOString().slice(0, 16).split("T");
    const day = days[new Date(date).getDay()];

    const effectiveGenderPref = ride?.genderPreference || ride?.host?.genderPreference || "ANY";
    const isHostFemale = ride?.host?.gender === "FEMALE";
    const isFemaleOnly = isHostFemale && effectiveGenderPref === "SAME";

    const handleConfirmOpenToAll = async () => {
        try {
            setUpdatingPref(true);
            const res = await updateRideApi(ride.id, { genderPreference: "ANY" });
            if (res) {
                setShowWarningModal(false);
                if (onRideUpdated) onRideUpdated();
            }
        } catch (err) {
            console.error("handleConfirmOpenToAll:", err);
        } finally {
            setUpdatingPref(false);
        }
    };

    const handleSwitchToFemaleOnly = async () => {
        try {
            setUpdatingPref(true);
            const res = await updateRideApi(ride.id, { genderPreference: "SAME" });
            if (res) {
                if (onRideUpdated) onRideUpdated();
            }
        } catch (err) {
            console.error("handleSwitchToFemaleOnly:", err);
        } finally {
            setUpdatingPref(false);
        }
    };

    const handleCancelRide = async () => {
        const response = await cancelRideApi(ride.id);
        if (response) {
            navigate("/my-rides");
        }
    };

    return (
        <>
            <RidePreferenceWarningModal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                onConfirm={handleConfirmOpenToAll}
                loading={updatingPref}
            />
            <Card className="w-full max-w-xl mx-auto my-6">
                <CardHeader className="pb-2">
                    <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                        Ride Details
                        {isFemaleOnly && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-pink-50 text-pink-600 border border-pink-200">
                                <FiShield className="w-3.5 h-3.5" /> Female Only
                            </span>
                        )}
                    </CardTitle>
                <CardDescription className="flex flex-wrap gap-4 mt-2 text-base md:text-lg">
                    <span className="flex items-center gap-1"><FiMapPin className="w-4 h-4 text-zinc-500" /><span className="font-medium">{ride?.from}</span> → <span className="font-medium">{ride?.to}</span></span>
                    <span className="flex items-center gap-1"><FiCalendar className="w-4 h-4 text-zinc-500" /><span className="font-medium">{date}</span> <span className="text-xs">({day})</span></span>
                    <span className="flex items-center gap-1"><FiClock className="w-4 h-4 text-zinc-500" /><span className="font-medium">{time}</span></span>
                </CardDescription>
            </CardHeader>
            <Separator className="my-1" />
            <CardContent className="flex flex-col gap-2 md:gap-3 text-zinc-800">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <span className="text-base md:text-lg font-medium text-zinc-700 flex items-center gap-1 flex-wrap">
                        <FiUser className="w-5 h-5 text-zinc-500" /> Host: <span className="font-semibold text-zinc-900">{ride.host.name}</span>
                        <GenderBadge gender={ride.host?.gender} />
                        <DesignationBadge email={ride.host?.email} />
                    </span>
                </div>
                <div className="flex flex-wrap gap-4 mt-2">
                    <span className="flex items-center gap-1"><FiUsers className="w-4 h-4 text-zinc-500" /><span className="font-medium">{ride?.capacity}</span> passengers</span>
                    <span className="flex items-center gap-1"><FiCreditCard className="w-4 h-4 text-zinc-500" /><span className="font-medium">{ride?.totalFare ? `₹${ride.totalFare}` : "TBD"}</span> per passenger</span>
                    <span className="flex items-center gap-1"><FiNavigation className="w-4 h-4 text-zinc-500" /><span className="font-medium">{ride?.vehicleType.charAt(0).toUpperCase() +
                        ride?.vehicleType.toLowerCase().slice(1)}</span></span>

                </div>
                {ride?.description && (
                    <div className="text-sm md:text-base text-zinc-600 mt-1 overflow-x-hidden text-wrap text-ellipsis ">
                        <span className="flex items-start gap-1"><FiFileText className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" /> <span>{ride?.description}</span></span>
                    </div>
                )}
                {isHostFemale && (
                    <div className="mt-2 pt-2 border-t border-zinc-100">
                        {isFemaleOnly ? (
                            <div className="p-3 sm:p-3.5 rounded-xl border border-pink-200 bg-pink-50/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="p-2 rounded-lg bg-pink-100 text-pink-700 shrink-0 mt-0.5 sm:mt-0">
                                        <FiShield className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-pink-900">Female-Only Ride</span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-pink-200/80 text-pink-800">
                                                Active
                                            </span>
                                        </div>
                                        <p className="text-xs text-pink-700 mt-0.5">
                                            Got no female co-passengers yet? You can open this ride to passengers of any gender.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowWarningModal(true)}
                                    className="w-full sm:w-auto shrink-0 px-3.5 py-1.5 text-xs font-semibold text-zinc-900 bg-white hover:bg-amber-500 hover:text-white border border-pink-300 hover:border-amber-500 rounded-lg shadow-sm transition-all"
                                >
                                    Open to Anyone
                                </button>
                            </div>
                        ) : (
                            <div className="p-3 sm:p-3.5 rounded-xl border border-zinc-200 bg-zinc-50/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="p-2 rounded-lg bg-amber-100 text-amber-800 shrink-0 mt-0.5 sm:mt-0">
                                        <FiUsers className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-zinc-900">Open to Any Gender</span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700">
                                                Unbiased
                                            </span>
                                        </div>
                                        <p className="text-xs text-zinc-600 mt-0.5">
                                            Passengers of any gender can discover and apply for this ride.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    disabled={updatingPref}
                                    onClick={handleSwitchToFemaleOnly}
                                    className="w-full sm:w-auto shrink-0 px-3.5 py-1.5 text-xs font-semibold text-pink-700 bg-white hover:bg-pink-500 hover:text-white border border-pink-200 rounded-lg shadow-sm transition-all"
                                >
                                    {updatingPref ? "Updating..." : "Switch to Female Only"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
            <CardFooter className="flex flex-col md:flex-row gap-2 md:justify-end mt-2">
                <button
                    onClick={() => { setEdit(true) }}
                    className="rounded-lg border border-input px-4 py-2 bg-background hover:bg-accent transition-colors text-sm font-medium"
                >
                    Edit Ride Details
                </button>
                <button
                    onClick={handleCancelRide}
                    className="rounded-lg border border-red-700 px-4 py-2 bg-red-500 text-white flex items-center gap-2 hover:bg-red-600 transition-colors text-sm font-medium"
                >
                    <FaTrashCan /> Cancel Ride
                </button>
            </CardFooter>
        </Card>
        </>
    );
};
